import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminSupplyChains } from '@/api/(admin)/supply-chains';

interface Args {
  companyId: string;
  /** 1-based, as the table counts pages. */
  page: number;
  limit: number;
  sort?: string;
  filters?: Record<string, string[]>;
}

/**
 * The supply chains this company owns — shared by the tab's route loader and
 * its hook, so the prefetch warms the key the component reads.
 *
 * `companyId` is a query param of its own here, not part of the `filter` JSON —
 * unlike the users list, where the company is one of the filtered columns.
 */
export function companySupplyChainsQueryOptions({
  companyId,
  page,
  limit,
  sort,
  filters,
}: Args) {
  const qs = new URLSearchParams({
    page: String(Math.max(0, page - 1)),
    count: String(limit),
    companyId,
  });
  if (sort) qs.set('sort', sort);

  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters ?? {})) {
    const value = values?.[0];
    if (value) filter[key] = value;
  }
  if (Object.keys(filter).length > 0) qs.set('filter', JSON.stringify(filter));

  return queryOptions({
    queryKey: [
      'admin',
      'supply-chains',
      companyId,
      page,
      limit,
      sort ?? null,
      filter,
    ] as const,
    queryFn: () => fetchAdminSupplyChains(qs.toString()),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

export function useCompanySupplyChains(args: Args) {
  const query = useQuery(companySupplyChainsQueryOptions(args));

  return {
    rows: query.data?.data ?? [],
    totalCount: query.data?.metadata?.pagination?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
